import {
  Button,
  ButtonGroup,
  Container,
  Content,
  DeleteButton,
  HeaderContainer,
  LoadingMessage,
  SearchContainer,
  SearchInput,
  SelectionCount,
  Table,
  TableContainer,
} from '@/components/CommonStyles';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
  ColumnDef,
  Row,
  Header,
  HeaderGroup,
  Cell,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface CommonSpaceListContainerProps<T extends { id: number }> {
  HeaderComponent: React.ComponentType;
  queryKey: string;
  queryFn: () => Promise<any>;
  columns: () => ColumnDef<T, any>[];
  baseRoute: string;
  buttonColor?: 'avnfr' | 'resom';
  onCreate?: () => void;
  onDelete?: (selectedIds: number[]) => Promise<unknown> | void;
  showCreateButton?: boolean;
}

function CommonSpaceListContainer<T extends { id: number }>({
  HeaderComponent,
  queryKey,
  queryFn,
  columns: getColumns,
  baseRoute,
  buttonColor,
  onCreate,
  onDelete,
  showCreateButton = false,
}: CommonSpaceListContainerProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: dataAPI, isLoading: isLoadingAPI } = useQuery({
    queryKey: [queryKey],
    queryFn: queryFn,
    staleTime: 1000 * 60 * 5,
    refetchOnMount: 'always', // 페이지 진입 시 항상 refetch
    enabled: true,
  });

  const columns = useMemo(() => {
    const baseColumns = getColumns();
    if (!onDelete) return baseColumns;

    const selectionColumn: ColumnDef<T, unknown> = {
      id: 'checkbox',
      header: ({ table }) => {
        const visibleIds = table
          .getRowModel()
          .rows.map((row) => row.original.id);
        const allVisibleSelected =
          visibleIds.length > 0 &&
          visibleIds.every((itemId) => selectedIds.includes(itemId));

        return (
          <input
            type="checkbox"
            checked={allVisibleSelected}
            onClick={(e) => e.stopPropagation()}
            onChange={() => {
              setSelectedIds((prev) => {
                if (allVisibleSelected) {
                  return prev.filter((itemId) => !visibleIds.includes(itemId));
                }

                return Array.from(new Set([...prev, ...visibleIds]));
              });
            }}
          />
        );
      },
      cell: ({ row }) => {
        const rowId = row.original.id;

        return (
          <input
            type="checkbox"
            checked={selectedIds.includes(rowId)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => {
              setSelectedIds((prev) =>
                prev.includes(rowId)
                  ? prev.filter((itemId) => itemId !== rowId)
                  : [...prev, rowId]
              );
            }}
          />
        );
      },
      enableSorting: false,
    };

    return [selectionColumn, ...baseColumns];
  }, [getColumns, onDelete, selectedIds]);

  const table = useReactTable({
    data: dataAPI?.data?.result || dataAPI?.data?.data || [],
    columns,
    state: {
      sorting,
      globalFilter: searchTerm,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setSearchTerm,
    globalFilterFn: (row, columnId, filterValue) => {
      const value = String(row.getValue(columnId) || '').toLowerCase();
      return value.includes(String(filterValue).toLowerCase());
    },
  });

  const handleRowClick = (row: T) => {
    navigate(`${baseRoute}/${row.id}`);
  };

  const handleDeleteSelected = async () => {
    if (!onDelete || selectedIds.length === 0) return;

    const confirmed = window.confirm(
      `${selectedIds.length}개 항목을 삭제하시겠습니까?`
    );
    if (!confirmed) return;

    await onDelete(selectedIds);
    setSelectedIds([]);
    queryClient.invalidateQueries({ queryKey: [queryKey] });
  };

  return (
    <Container>
      <HeaderComponent />
      <Content>
        {isLoadingAPI ? (
          <LoadingMessage>데이터를 불러오는 중입니다...</LoadingMessage>
        ) : !(dataAPI?.data?.result || dataAPI?.data?.data) ? (
          <LoadingMessage>
            데이터가 없습니다. 잠시 후 다시 시도해주세요.
          </LoadingMessage>
        ) : (
          <>
            <HeaderContainer>
              <SearchContainer>
                <SearchInput
                  type="text"
                  placeholder="검색어를 입력하세요..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </SearchContainer>
              <ButtonGroup>
                {onDelete && selectedIds.length > 0 && (
                  <>
                    <SelectionCount>
                      {selectedIds.length}개 선택됨
                    </SelectionCount>
                    <DeleteButton onClick={handleDeleteSelected}>
                      삭제
                    </DeleteButton>
                  </>
                )}
                {showCreateButton && onCreate && (
                  <Button type={buttonColor} onClick={onCreate}>
                    생성
                  </Button>
                )}
              </ButtonGroup>
            </HeaderContainer>
            <TableContainer>
              <Table>
                <thead>
                  {table
                    .getHeaderGroups()
                    .map((headerGroup: HeaderGroup<T>) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map(
                          (header: Header<T, unknown>) => (
                            <th
                              key={header.id}
                              onClick={header.column.getToggleSortingHandler()}
                              style={{ cursor: 'pointer' }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: ' 🔼',
                                desc: ' 🔽',
                              }[header.column.getIsSorted() as string] ?? null}
                            </th>
                          )
                        )}
                      </tr>
                    ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row: Row<T>) => (
                    <tr
                      key={row.id}
                      onClick={() => handleRowClick(row.original)}
                      style={{ cursor: 'pointer' }}
                    >
                      {row.getVisibleCells().map((cell: Cell<T, unknown>) => (
                        <td
                          key={cell.id}
                          onClick={
                            cell.column.id === 'checkbox'
                              ? (e) => e.stopPropagation()
                              : undefined
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          </>
        )}
      </Content>
    </Container>
  );
}

export default CommonSpaceListContainer;
